import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import bytes from 'bytes';
import { fromEvent } from 'file-selector';
import { useCallback, useMemo, useRef } from 'react';
import {
  FileType,
  ImageFileSizeRange,
  ImageSupportedExt,
  ImageSupportedMimeTypes
} from '@_/constants';
import { getFileMimeType, isIeOrEdge } from '@_/utils-frontend';

export interface FileSelectorProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fileType: FileType;
  inputProps?: React.ComponentPropsWithoutRef<'input'>;
  onSelectFile: (payload: { file: File; mimeType: string }) => void;
  render?: useRender.ComponentProps<
    'button',
    object,
    React.ButtonHTMLAttributes<HTMLElement>
  >['render'];
}

export const FileSelector = ({
  children,
  className,
  disabled,
  fileType,
  inputProps,
  onSelectFile,
  render
}: FileSelectorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const sizeRange = useMemo(
    () =>
      fileType === FileType.Image ? ImageFileSizeRange : { min: bytes('0B')!, max: bytes('0B')! },
    [fileType]
  );

  const mimeTypes = useMemo(
    () => (fileType === FileType.Image ? ImageSupportedMimeTypes : ['*']),
    [fileType]
  );
  const extensions = useMemo(
    () => (fileType === FileType.Image ? ImageSupportedExt : []),
    [fileType]
  );

  const openFileDialog = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  }, []);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    if (isIeOrEdge()) {
      setTimeout(() => {
        openFileDialog();
      }, 0);
    } else {
      openFileDialog();
    }
  }, [disabled, openFileDialog]);

  const handleSelectFile = useCallback<React.ChangeEventHandler>(
    async event => {
      const result = await fromEvent(event);

      if (!result.length) {
        return;
      }

      const file = result[0] instanceof DataTransferItem ? result[0].getAsFile() : result[0];

      if (!file) {
        return;
      }

      event.persist();

      const mime = await getFileMimeType(file);

      if (!mimeTypes.includes(mime) || file.size < sizeRange.min || file.size > sizeRange.max) {
        return;
      }

      onSelectFile({ file, mimeType: mime });
    },
    [mimeTypes, onSelectFile, sizeRange]
  );

  return useRender({
    defaultTagName: 'button',
    render,
    props: mergeProps<'button'>(
      {
        className,
        'aria-disabled': disabled ? true : undefined,
        disabled,
        onClick: handleClick,
        role: 'button',
        tabIndex: -1,
        type: 'button'
      },
      {
        children: (
          <>
            <input
              {...inputProps}
              accept={`${[mimeTypes, ...extensions].join(',')}`}
              className="hidden"
              disabled={disabled}
              onChange={handleSelectFile}
              ref={inputRef}
              tabIndex={-1}
              type="file"
            />
            {children}
          </>
        )
      }
    )
  });
};
